import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { TenantEnum } from '@jsmdg/tenancy';
import {
    AlgoliaAnalyticsEventAction,
    AlgoliaAnalyticsEventPage,
    AlgoliaAnalyticsEventSource,
    sendAlgoliaFiltersViewedEvent,
} from '@jsmdg/tracking';
import { FilterDropdownBase, NumericRange, PriceFrancIcon, PriceIcon } from '@jsmdg/yoshi';
import { type FragmentContext } from '../../../../../../shared/types/fragmentContext';
import { type Filter as FilterType } from '../../../../../../shared/types/search';
import { PriceFilterDefault } from '../../../../../enums/priceFilterDefault';
import { formatAlgoliaPriceFilter } from '../../../../../helper/formatPrice';
import { usePriceFilterPLP as usePriceFilter } from '../../../../../hooks';
import { useInsightsClient } from '../../../../../hooks/useInsightsClient';
import { type SearchReducerActionType } from '../../../../../reducers/searchReducer';
import { type Nullable } from '../../../../../types';
import { FloatingTogglePriceMessage } from '../../../../PriceMessage';
import { InputPriceControls } from './InputPriceControls/InputPriceControls';
import styles from '../common.module.scss';

const PRICE_BOUNDARY_MIN = PriceFilterDefault.Min;
const PRICE_BOUNDARY_MAX = PriceFilterDefault.Max;

const messages = defineMessages({
    label: {
        defaultMessage: 'Wie viel?',
    },
    dropdownLabel: {
        defaultMessage: 'Alle Preise',
    },
    submitLabel: {
        defaultMessage: 'Fertig',
    },
    resetLabel: {
        defaultMessage: 'Zurücksetzen',
    },
    minimumValue: {
        defaultMessage: 'Mindestpreis',
    },
    maximumValue: {
        defaultMessage: 'Höchstpreis',
    },
    a11yPriceFilter: {
        defaultMessage: 'Preis eingeben',
    },
});

type PriceFilterProps = {
    readonly currencyCode: string;
    readonly onSubmit: (
        label: SearchReducerActionType,
        value: { min: Nullable<number>; max: Nullable<number> } | Nullable<number>,
    ) => void;
    readonly values: Array<Nullable<number>>;
    readonly filter: FilterType;
    readonly scrollToElement: () => void;
};

const PriceFilter = ({
    currencyCode,
    filter,
    onSubmit,
    scrollToElement,
    values,
}: PriceFilterProps): JSX.Element => {
    const {
        handleOnClickOutside,
        handleReset,
        inputsValues,
        onInputUpdate,
        onPriceChange,
        onRangeChange,
        priceData,
        rangeValues,
        sliderDidUpdate,
    } = usePriceFilter({
        currencyCode,
        onSubmit,
        options: { min: PRICE_BOUNDARY_MIN, max: PRICE_BOUNDARY_MAX },
        values,
        filter,
    });

    const { algoliaTracking, tenant } = useFragmentContext<FragmentContext>();
    const insightsClientRef = useInsightsClient();
    const intl = useIntl();
    const hasPriceSelected = !priceData.isDefaultMin || !priceData.isDefaultMax;

    const PriceComponent = tenant === TenantEnum.MD_CH ? PriceFrancIcon : PriceIcon;

    const handleOnOpen = async (): Promise<void> => {
        scrollToElement();

        if (!algoliaTracking?.userToken || !algoliaTracking.indexName) {
            return;
        }

        await window.yieldToMainThread?.();

        try {
            const insightsClient = (await insightsClientRef.current)?.default;

            if (!insightsClient) {
                return;
            }

            sendAlgoliaFiltersViewedEvent({
                eventName: {
                    action: AlgoliaAnalyticsEventAction.FilterViewed,
                    page: AlgoliaAnalyticsEventPage.Home,
                    source: AlgoliaAnalyticsEventSource.ExperienceFinder,
                },
                filters: [
                    formatAlgoliaPriceFilter(
                        priceData.price[0],
                        priceData.price[1],
                        PRICE_BOUNDARY_MAX,
                    ),
                ],
                indexName: algoliaTracking.indexName,
                insightsClient,
            });
        } catch (error) {
            logToSentry(error instanceof Error ? error : new Error('Algolia Insights failed'));
        }
    };

    const getPriceLabel = (): JSX.Element | string => {
        if (hasPriceSelected) {
            return <FloatingTogglePriceMessage priceData={priceData} />;
        }

        return intl.formatMessage(messages.dropdownLabel);
    };

    return (
        <div
            className={classNames(styles.wrapper, 'd-flex flex-sm-column w-100 p-0', {
                [styles.isActive]: hasPriceSelected,
            })}
            data-testid="experience-finder-price-filter"
        >
            <div className="d-flex align-items-start">
                <PriceComponent ariaHidden className={classNames(styles.icon, 'ml-1x mr-0-5x')} />
                <p className={classNames(styles.label, 'd-none d-sm-block fw-semibold mb-0')}>
                    {intl.formatMessage(messages.label)}
                </p>
            </div>

            <FilterDropdownBase
                className={styles.filterDropdown}
                label={getPriceLabel()}
                resetLabel={intl.formatMessage(messages.resetLabel)}
                submitLabel={intl.formatMessage(messages.submitLabel)}
                onSubmit={() => {
                    onPriceChange(rangeValues);
                }}
                onReset={handleReset}
                onClickOutside={handleOnClickOutside}
                hasChanged={sliderDidUpdate}
                showNumSelected={false}
                numSelected={!priceData.price[0] && !priceData.price[1] ? 0 : 1}
                filterDropdownButtonClasses={classNames(
                    styles.filterButton,
                    'w-100 py-0 pl-0-5x pl-sm-2x justify-content-between',
                    { [styles.placeholder]: !hasPriceSelected },
                )}
                filterDropdownOverlayClasses={classNames(
                    styles.filterDropdownOverlay,
                    'mt-2x mt-sm-1-5x',
                )}
                onOpen={handleOnOpen}
                a11ySubmitText={intl.formatMessage(messages.submitLabel)}
                a11yButtonText={intl.formatMessage(messages.a11yPriceFilter)}
                a11yIndicatorText={intl.formatMessage(messages.resetLabel)}
            >
                <div className="px-3x">
                    <InputPriceControls inputsValues={inputsValues} onInputUpdate={onInputUpdate} />

                    <NumericRange
                        values={rangeValues}
                        onChange={((val: number[]) => onRangeChange(val, true)) as () => void}
                        options={[PRICE_BOUNDARY_MIN, PRICE_BOUNDARY_MAX]}
                        step={10}
                        pushable={10}
                        a11yText={[
                            intl.formatMessage(messages.minimumValue),
                            intl.formatMessage(messages.maximumValue),
                        ]}
                    />
                </div>
            </FilterDropdownBase>
        </div>
    );
};

export { PriceFilter };
